import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, MessageCircle, CalendarDays, X } from "lucide-react";
import { Reveal, ChapterHeader } from "./Reveal";
import { CONTACT } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES_OPTIONS = [
  "Individual therapy",
  "Couples session",
  "Family session",
  "Psychometric assessment",
  "Psycho-legal / medico-legal assessment",
  "Psychoeducational assessment",
  "General enquiry",
];

const LOCATIONS = [
  {
    id: "pawc",
    label: "PAWC, Bryanston",
    note: "Therapy, assessments and everything else",
    days: [1, 2, 4, 5, 6], // Mon, Tue, Thu, Fri, Sat
  },
  {
    id: "wits",
    label: "Wits University, Emtonjeni Centre",
    note: "Assessments only, adult or child",
    days: [3], // Wednesdays
  },
];

const SESSION_TYPES = [
  { id: "Individual / adult therapy", length: "50 to 60 minutes", hint: "R1,292.20" },
  { id: "Play therapy", length: "50 to 60 minutes", hint: "R1,292.20" },
  { id: "Couples / family", length: "80 to 90 minutes", hint: "R1,722.80" },
  { id: "Full assessment", length: "about 6 hours across intake, testing and feedback", hint: "R7,753.20 in total" },
];

const TIME_SLOTS = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];

const inputCls =
  "w-full rounded-xl border border-pawc-teal/20 bg-white px-4 py-3.5 text-sm text-pawc-ink placeholder:text-pawc-slate/50 focus:outline-none focus:ring-2 focus:ring-pawc-teal/40 transition-shadow duration-300";

const dayLabel = (d) =>
  d.toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short" });

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [location, setLocation] = useState("pawc");
  const [sessionType, setSessionType] = useState(null);
  const [prefDay, setPrefDay] = useState(null);
  const [prefTime, setPrefTime] = useState(null);
  const [sending, setSending] = useState(false);

  const loc = LOCATIONS.find((l) => l.id === location);

  const days = useMemo(() => {
    const list = [];
    const d = new Date();
    while (list.length < 10) {
      d.setDate(d.getDate() + 1);
      if (loc.days.includes(d.getDay())) list.push(new Date(d));
    }
    return list;
  }, [loc]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const clearBooking = () => {
    setPrefDay(null);
    setPrefTime(null);
    setSessionType(null);
    setLocation("pawc");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        preferred_day: prefDay ? dayLabel(prefDay) : "",
        preferred_time: prefTime || "",
        preferred_location: prefDay ? loc.label : "",
        session_type: prefDay
          ? location === "wits"
            ? "Assessment (adult or child)"
            : sessionType?.id || ""
          : "",
        session_length: prefDay && location === "pawc" ? sessionType?.length || "" : "",
      });
      toast.success("Thank you, your message has been received. Clive will be in touch soon.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
      clearBooking();
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again, or reach out by phone or WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const summaryText = prefDay && prefTime
    ? `${dayLabel(prefDay)} at ${prefTime} · ${loc.label}${
        location === "wits"
          ? " · Assessment (adult or child)"
          : sessionType
            ? ` · ${sessionType.id}, ${sessionType.length}`
            : ""
      }`
    : "";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-pawc-paper">
      <div className="max-w-6xl mx-auto px-6">
        <ChapterHeader number="08" eyebrow="Contact" title="Begin the conversation" />

        <div className="grid lg:grid-cols-2 gap-12 mt-14">
          <div className="space-y-8">
            <Reveal>
              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <MapPin size={18} strokeWidth={1.5} className="text-pawc-terra mt-1 shrink-0" />
                  <div>
                    <p className="text-pawc-deep font-medium">{CONTACT.practice}</p>
                    <p className="text-pawc-slate text-sm">{CONTACT.address}</p>
                    <p className="text-pawc-slate text-sm mt-2">
                      <span className="text-pawc-deep font-medium">Emtonjeni Centre, Wits University:</span>{" "}
                      assessments only, adult or child
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone size={18} strokeWidth={1.5} className="text-pawc-terra mt-1 shrink-0" />
                  <a
                    data-testid="contact-phone-link"
                    href={`tel:${CONTACT.phoneIntl}`}
                    className="text-pawc-slate text-sm hover:text-pawc-teal transition-colors duration-300"
                  >
                    {CONTACT.phone}
                  </a>
                </li>
                <li className="flex items-start gap-4">
                  <Mail size={18} strokeWidth={1.5} className="text-pawc-terra mt-1 shrink-0" />
                  <a
                    data-testid="contact-email-link"
                    href={`mailto:${CONTACT.email}`}
                    className="text-pawc-slate text-sm hover:text-pawc-teal transition-colors duration-300"
                  >
                    {CONTACT.email}
                  </a>
                </li>
                <li className="flex items-start gap-4">
                  <Clock size={18} strokeWidth={1.5} className="text-pawc-terra mt-1 shrink-0" />
                  <div className="text-pawc-slate text-sm space-y-1">
                    <p>
                      <span className="text-pawc-deep font-medium">{CONTACT.practice}, Bryanston:</span>{" "}
                      {CONTACT.pawcDays}
                    </p>
                    <p>
                      <span className="text-pawc-deep font-medium">Wits University:</span>{" "}
                      {CONTACT.witsDays}
                    </p>
                    <p>Sessions by appointment. Parking and access details are shared when you book.</p>
                  </div>
                </li>
              </ul>
            </Reveal>

            <Reveal delay={0.1}>
              <a
                data-testid="contact-whatsapp-btn"
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full bg-pawc-teal hover:bg-pawc-deep text-pawc-sand px-7 py-3.5 text-sm transition-colors duration-300"
              >
                <MessageCircle size={18} strokeWidth={1.5} />
                Chat on WhatsApp
              </a>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="rounded-2xl overflow-hidden border border-pawc-teal/15 shadow-[0_20px_40px_-15px_rgba(14,95,115,0.08)]">
                <iframe
                  data-testid="contact-map"
                  title="Map: 21 Shepherd Ave, Bryanston, Johannesburg"
                  src="https://www.google.com/maps?q=21+Shepherd+Ave,+Bryanston,+Johannesburg&output=embed"
                  className="w-full h-[280px] border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-3xl bg-pawc-sand border border-pawc-teal/10 p-8 md:p-10 shadow-[0_20px_40px_-15px_rgba(14,95,115,0.08)] space-y-5"
            >
              <p className="font-serif text-2xl text-pawc-deep mb-2">Send a message</p>

              <div className="rounded-2xl border border-pawc-teal/15 bg-pawc-mist/40 p-5">
                <p className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-pawc-teal mb-4">
                  <CalendarDays size={14} strokeWidth={1.5} />
                  Request a preferred time (optional)
                </p>

                {summaryText ? (
                  <div
                    data-testid="booking-selection"
                    className="flex items-center justify-between rounded-xl bg-white border border-pawc-teal/20 px-4 py-3"
                  >
                    <p className="text-sm text-pawc-deep">{summaryText}</p>
                    <button
                      type="button"
                      data-testid="booking-clear-btn"
                      onClick={clearBooking}
                      aria-label="Clear preferred time"
                      className="text-pawc-slate hover:text-pawc-terra transition-colors duration-300 ml-3 shrink-0"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid sm:grid-cols-2 gap-2 mb-4">
                      {LOCATIONS.map((l) => (
                        <button
                          key={l.id}
                          type="button"
                          data-testid={`booking-location-${l.id}`}
                          onClick={() => { setLocation(l.id); setPrefDay(null); setPrefTime(null); }}
                          className={`rounded-xl border p-4 text-left transition-colors duration-300 ${
                            location === l.id
                              ? "bg-pawc-teal text-pawc-sand border-pawc-teal"
                              : "bg-white text-pawc-slate border-pawc-teal/20 hover:border-pawc-teal/50"
                          }`}
                        >
                          <span className="block text-sm font-medium">{l.label}</span>
                          <span className={`block text-[11px] mt-1 ${location === l.id ? "text-pawc-mist/75" : "text-pawc-slate/70"}`}>
                            {l.note}
                          </span>
                        </button>
                      ))}
                    </div>

                    {location === "pawc" && (
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {SESSION_TYPES.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            data-testid={`booking-type-${s.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => setSessionType(s)}
                            className={`rounded-xl border p-3.5 text-left transition-colors duration-300 ${
                              sessionType?.id === s.id
                                ? "bg-pawc-deep text-pawc-sand border-pawc-deep"
                                : "bg-white text-pawc-slate border-pawc-teal/20 hover:border-pawc-teal/50"
                            }`}
                          >
                            <span className="block text-sm font-medium">{s.id}</span>
                            <span className={`block text-[11px] mt-0.5 ${sessionType?.id === s.id ? "text-pawc-mist/75" : "text-pawc-slate/70"}`}>
                              {s.length}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    {location === "pawc" && sessionType && (
                      <p className="text-[11px] text-pawc-teal mb-4">
                        {sessionType.id}: {sessionType.hint}, at current Discovery medical aid rates.
                      </p>
                    )}
                    {location === "wits" && (
                      <p className="text-[11px] text-pawc-slate/80 mb-4">
                        Emtonjeni Centre sessions are assessments only, for adults or children, on
                        Wednesdays. Medical aid rates apply when paying through medical aid; cash
                        fees are agreed together in discussion.
                      </p>
                    )}

                    <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                      {days.map((d) => (
                        <button
                          key={d.toISOString()}
                          type="button"
                          data-testid={`booking-day-${d.getDate()}`}
                          onClick={() => setPrefDay(d)}
                          className={`shrink-0 rounded-full px-4 py-2 text-xs border transition-colors duration-300 ${
                            prefDay && d.getTime() === prefDay.getTime()
                              ? "bg-pawc-teal text-pawc-sand border-pawc-teal"
                              : "bg-white text-pawc-slate border-pawc-teal/20 hover:border-pawc-teal/50"
                          }`}
                        >
                          {dayLabel(d)}
                        </button>
                      ))}
                    </div>
                    {prefDay && (
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {TIME_SLOTS.map((t) => (
                          <button
                            key={t}
                            type="button"
                            data-testid={`booking-time-${t.replace(":", "")}`}
                            onClick={() => setPrefTime(t)}
                            className={`rounded-full px-3 py-2 text-xs border transition-colors duration-300 ${
                              prefTime === t
                                ? "bg-pawc-terra text-white border-pawc-terra"
                                : "bg-white text-pawc-slate border-pawc-teal/20 hover:border-pawc-terra/50"
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-pawc-slate/70 mt-3">
                      This is a request, not a confirmed booking. Clive will confirm your session time and exact fee directly.
                    </p>
                  </>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className={inputCls}
                  aria-label="Your name"
                />
                <input
                  data-testid="contact-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Email address"
                  className={inputCls}
                  aria-label="Email address"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Phone (optional)"
                  className={inputCls}
                  aria-label="Phone number"
                />
                <select
                  data-testid="contact-service-select"
                  value={form.service}
                  onChange={set("service")}
                  className={inputCls}
                  aria-label="What is this regarding?"
                >
                  <option value="">What is this regarding?</option>
                  {SERVICES_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <textarea
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="A few words about what brings you here, shared in confidence."
                className={`${inputCls} resize-none`}
                aria-label="Your message"
              />
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="w-full rounded-full bg-pawc-terra hover:bg-pawc-terraDark disabled:opacity-60 text-white py-4 text-sm tracking-wide transition-colors duration-300"
              >
                {sending ? "Sending…" : "Send Message"}
              </button>
              <p className="text-xs text-pawc-slate/70 text-center">
                Prefer to call or email? Reach Clive directly on {CONTACT.phone} or{" "}
                {CONTACT.email}.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
